import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import ItemCard from "@/components/ItemCard";

export default function Recommendations() {
  const [items, setItems] = useState([]);
  const [message, setMessage] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/recommendations")
      .then(r => {
        setItems(r.data.items || []);
        setMessage(r.data.message || "");
        setCategories(r.data.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-[#D96C4A]/10 flex items-center justify-center">
            <Sparkle size={24} weight="duotone" className="text-[#D96C4A]" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight" data-testid="recommendations-heading">
              Recommended for You
            </h1>
          </div>
        </div>

        {/* AI Message */}
        {message && (
          <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5 mb-8 max-w-2xl" data-testid="ai-recommendation-message">
            <div className="flex items-start gap-3">
              <Sparkle size={18} weight="fill" className="text-[#D96C4A] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[#5C665D] leading-relaxed">{message}</p>
            </div>
          </div>
        )}

        {/* Interest Categories */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <span className="text-xs font-semibold text-[#5C665D] uppercase tracking-wider self-center mr-2">Your interests:</span>
            {categories.map(cat => (
              <Link
                key={cat}
                to={`/browse?category=${cat}`}
                className="bg-[#2C5545]/10 text-[#2C5545] rounded-full px-3 py-1 text-xs font-medium hover:bg-[#2C5545]/20 transition-colors capitalize"
                data-testid={`interest-cat-${cat}`}
              >
                {cat.replace(/_/g, " ")}
              </Link>
            ))}
          </div>
        )}

        {/* Items */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#2C5545] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#EAE7E0] rounded-2xl" data-testid="recommendations-empty">
            <Sparkle size={48} weight="duotone" className="text-[#EAE7E0] mx-auto mb-4" />
            <p className="font-heading text-2xl text-[#1A1C1A] mb-2">No recommendations yet</p>
            <p className="text-sm text-[#5C665D] mb-6">Browse and interact with items to get personalized suggestions.</p>
            <Link to="/browse" className="bg-[#2C5545] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#224235] transition-all inline-flex items-center gap-2" data-testid="recommendations-browse-link">
              Start Browsing <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {items.map(item => <ItemCard key={item.id} item={item} />)}
          </div>
        )}
      </div>
    </main>
  );
}
